import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ReferenceLine } from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Activity, Heart, Thermometer, Droplets, Weight, HeartPulse } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface VitalReading {
  executed_at: string;
  vital_value: number;
  vital_value_secondary?: number | null;
}

interface VitalSignsChartProps {
  vitalType: string;
  readings: VitalReading[];
  minAlertValue?: number | null;
  maxAlertValue?: number | null;
  unit?: string;
}

const VITAL_CONFIG_KEYS: Record<string, { labelKey: string; icon: React.ElementType; color: string }> = {
  blood_pressure: { labelKey: 'vitals.bloodPressure', icon: Heart, color: 'hsl(var(--destructive))' },
  glucose: { labelKey: 'vitals.glucose', icon: Droplets, color: 'hsl(var(--primary))' },
  temperature: { labelKey: 'vitals.temperature', icon: Thermometer, color: 'hsl(var(--warning))' },
  oxygen_saturation: { labelKey: 'vitals.oxygenSaturation', icon: Activity, color: 'hsl(var(--success))' },
  weight: { labelKey: 'vitals.weight', icon: Weight, color: 'hsl(var(--secondary))' },
  heart_rate: { labelKey: 'vitals.heartRate', icon: HeartPulse, color: 'hsl(var(--destructive))' }
};

export function VitalSignsChart({
  vitalType,
  readings,
  minAlertValue,
  maxAlertValue,
  unit
}: VitalSignsChartProps) {
  const { t } = useTranslation();
  const config = VITAL_CONFIG_KEYS[vitalType] || { labelKey: 'common.details', icon: Activity, color: 'hsl(var(--primary))' };
  const Icon = config.icon;
  const label = t(config.labelKey);

  const chartData = useMemo(() => {
    return readings
      .map(r => ({
        date: format(new Date(r.executed_at), 'dd/MM', { locale: ptBR }),
        fullDate: format(new Date(r.executed_at), "dd/MM 'às' HH:mm", { locale: ptBR }),
        value: r.vital_value,
        secondary: r.vital_value_secondary
      }));
  }, [readings]);

  const chartConfig = {
    value: {
      label: vitalType === 'blood_pressure' ? t('vitals.systolic') : label,
      color: config.color
    },
    secondary: {
      label: vitalType === 'blood_pressure' ? t('vitals.diastolic') : t('vitals.minValue'),
      color: 'hsl(var(--muted-foreground))'
    }
  };

  if (readings.length < 2) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: config.color }} />
          {label}
          {unit && <span className="text-muted-foreground font-normal">({unit})</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-2 pb-3">
        <ChartContainer config={chartConfig} className="h-[200px] w-full">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickMargin={4}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              tickLine={false}
              axisLine={false}
              width={40}
              domain={['auto', 'auto']}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(value, payload) => {
                    return payload?.[0]?.payload?.fullDate || value;
                  }}
                />
              }
            />

            {/* Safe range shading */}
            {minAlertValue && (
              <ReferenceLine
                y={minAlertValue}
                stroke="hsl(var(--warning))"
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />
            )}
            {maxAlertValue && (
              <ReferenceLine
                y={maxAlertValue}
                stroke="hsl(var(--warning))"
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />
            )}

            <Line
              type="monotone"
              dataKey="value"
              stroke={config.color}
              strokeWidth={2}
              dot={{ fill: config.color, strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5 }}
            />

            {vitalType === 'blood_pressure' && (
              <Line
                type="monotone"
                dataKey="secondary"
                stroke="hsl(var(--muted-foreground))"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: 'hsl(var(--muted-foreground))', strokeWidth: 0, r: 3 }}
              />
            )}
            <ChartLegend content={<ChartLegendContent />} />
          </LineChart>
        </ChartContainer>

        {(minAlertValue || maxAlertValue) && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            {t('executeTask.normalRange')}: {minAlertValue || '?'} - {maxAlertValue || '?'} {unit}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
